import { emptyArray } from 'ui-components/default-props/empty';
import type { CustomsDocument, CustomsDocumentsState } from './types';

// All selectors read from the customsDocuments slice (not root state).
// Registered in global-selectors.js via futzingMap.

export const selectCarrierCapabilities = (state: CustomsDocumentsState) => state.capabilities;
export const selectIsLoadingCapabilities = (state: CustomsDocumentsState) => state.isLoadingCapabilities;
export const selectCapabilitiesError = (state: CustomsDocumentsState) => state.capabilitiesError;

// ─── Order Documents ──────────────────────────────────────────────────────────

const selectOrderDocumentState = (state: CustomsDocumentsState, salesOrderId: string | null) =>
  salesOrderId ? (state.orderDocuments[salesOrderId] ?? null) : null;

export const selectOrderDocuments = (state: CustomsDocumentsState, salesOrderId: string | null): CustomsDocument[] =>
  selectOrderDocumentState(state, salesOrderId)?.documents ?? (emptyArray as CustomsDocument[]);
export const selectIsLoadingOrderDocuments = (state: CustomsDocumentsState, salesOrderId: string | null) =>
  selectOrderDocumentState(state, salesOrderId)?.isLoading ?? false;
export const selectIsUploadingOrderDocument = (state: CustomsDocumentsState, salesOrderId: string | null) =>
  selectOrderDocumentState(state, salesOrderId)?.isUploading ?? false;
export const selectOrderUploadProgress = (state: CustomsDocumentsState, salesOrderId: string | null) =>
  selectOrderDocumentState(state, salesOrderId)?.uploadProgress ?? 0;
export const selectOrderDocumentsError = (state: CustomsDocumentsState, salesOrderId: string | null) =>
  selectOrderDocumentState(state, salesOrderId)?.error ?? null;

// ─── Shipment Documents ───────────────────────────────────────────────────────

const selectShipmentDocumentState = (state: CustomsDocumentsState, shipmentId: string | null) =>
  shipmentId ? (state.shipmentDocuments[shipmentId] ?? null) : null;

export const selectShipmentDocuments = (state: CustomsDocumentsState, shipmentId: string | null): CustomsDocument[] =>
  selectShipmentDocumentState(state, shipmentId)?.documents ?? (emptyArray as CustomsDocument[]);
export const selectIsLoadingShipmentDocuments = (state: CustomsDocumentsState, shipmentId: string | null) =>
  selectShipmentDocumentState(state, shipmentId)?.isLoading ?? false;
export const selectIsUploadingShipmentDocument = (state: CustomsDocumentsState, shipmentId: string | null) =>
  selectShipmentDocumentState(state, shipmentId)?.isUploading ?? false;
export const selectIsSendingShipmentDocuments = (state: CustomsDocumentsState, shipmentId: string | null) =>
  selectShipmentDocumentState(state, shipmentId)?.isSending ?? false;
export const selectShipmentDocumentsError = (state: CustomsDocumentsState, shipmentId: string | null) =>
  selectShipmentDocumentState(state, shipmentId)?.error ?? null;

// ─── Derived ──────────────────────────────────────────────────────────────────

/**
 * Returns true if the carrier supports document upload.
 *
 * The capabilities API returns broad carrier IDs (e.g. "ups", "fedex", "ups_walleted").
 * The carrierApiCode from seller services can be more specific (e.g. "ups_globalpost_byoa_plus_single_piece").
 * Match by exact carrierId or by prefix (carrierApiCode starts with "carrierId_").
 */
export const selectCarrierSupportsDocumentUpload = (state: CustomsDocumentsState, carrierApiCode: string | null) => {
  const capabilities = selectCarrierCapabilities(state);
  if (!capabilities?.carriers || !carrierApiCode) return false;
  return capabilities.carriers.some((c) => c.carrierId === carrierApiCode || carrierApiCode.startsWith(`${c.carrierId}_`));
};

/**
 * Returns the base carrier ID (e.g. "fedex") for a given carrierApiCode (e.g. "fedex_international_economy").
 * The register document API expects the base carrier ID, not the full service-specific code.
 */
export const selectCarrierIdForUpload = (state: CustomsDocumentsState, carrierApiCode: string | null) => {
  const capabilities = selectCarrierCapabilities(state);
  if (!capabilities?.carriers || !carrierApiCode) return null;
  const carrier = capabilities.carriers.find((c) => c.carrierId === carrierApiCode || carrierApiCode.startsWith(`${c.carrierId}_`));
  return carrier?.carrierId ?? null;
};

/**
 * Returns the list of document types available for upload.
 */
export const selectSupportedDocumentTypes = (state: CustomsDocumentsState): string[] => {
  const capabilities = selectCarrierCapabilities(state);
  if (!capabilities) return emptyArray as string[];
  return (capabilities.document_types ?? capabilities.documentTypes ?? emptyArray) as string[];
};

/**
 * Returns true if a shipment has at least one document in uploaded/queued/sent status.
 */
export const selectShipmentHasSubmittedDocuments = (state: CustomsDocumentsState, shipmentId: string | null) => {
  const docs = selectShipmentDocuments(state, shipmentId);
  return docs.some((doc: CustomsDocument) => ['queued', 'sent'].includes(doc.status));
};

// ─── Pending Document Config ─────────────────────────────────────────────────

/**
 * Returns the optimistic pendingDocuments flag for a fulfillmentPlanId.
 * Returns null if no local override exists (components should fall back to CSW state).
 */
export const selectPendingDocuments = (state: CustomsDocumentsState, fulfillmentPlanId: string | null): boolean | null => {
  if (!fulfillmentPlanId) return null;
  return state.pendingDocuments?.[fulfillmentPlanId] ?? null;
};
